/**
 * Id do rascunho ATIVA na aba atual (sessionStorage), reconciliado com GET /ativa.
 */

import { buscarDetalheSimulacao, buscarSimulacaoAtiva, criarSimulacao } from "../simulacao.gateway";
import type { SimulacaoAtivaApi } from "../simulacao.gateway";

export const SIMULACAO_SESSAO_CHAVE = "hub.recursos.simulacaoAtiva";

export interface SimulacaoSessao {
  id: string;
  versao?: number;
  status?: string;
}

function normalizarId(id: string): string {
  return id.trim().toLowerCase();
}

export function lerSimulacaoSessao(): SimulacaoSessao | null {
  try {
    const bruto = sessionStorage.getItem(SIMULACAO_SESSAO_CHAVE);
    if (!bruto) return null;
    const parsed = JSON.parse(bruto) as unknown;
    if (typeof parsed !== "object" || parsed === null) return null;
    const id = (parsed as { id?: unknown }).id;
    if (typeof id !== "string" || !id.trim()) return null;
    const versao = (parsed as { versao?: unknown }).versao;
    const status = (parsed as { status?: unknown }).status;
    return {
      id: normalizarId(id),
      ...(typeof versao === "number" ? { versao } : {}),
      ...(typeof status === "string" ? { status } : {}),
    };
  } catch {
    return null;
  }
}

export function gravarSimulacaoSessao(
  ativa: Pick<SimulacaoAtivaApi, "id" | "versao" | "status">,
): void {
  if (ativa.status !== "ATIVA") return;
  const payload: SimulacaoSessao = {
    id: normalizarId(ativa.id),
    versao: ativa.versao,
    status: ativa.status,
  };
  sessionStorage.setItem(SIMULACAO_SESSAO_CHAVE, JSON.stringify(payload));
}

export function limparSimulacaoSessao(): void {
  sessionStorage.removeItem(SIMULACAO_SESSAO_CHAVE);
}

/** Alinha sessionStorage com o resultado de GET /ativa (ou limpa se não há rascunho). */
export function sincronizarSimulacaoSessao(ativa: SimulacaoAtivaApi | null): void {
  if (!ativa || ativa.status !== "ATIVA") {
    limparSimulacaoSessao();
    return;
  }
  gravarSimulacaoSessao(ativa);
}

/**
 * GET /ativa na entrada da Carga de Trabalho. Se o endpoint vier 204 mas a aba
 * ainda guarda um id ATIVA, revalida pelo detalhe antes de descartar a sessão.
 */
export async function carregarSimulacaoAtivaInicial(
  signal?: AbortSignal,
): Promise<SimulacaoAtivaApi | null> {
  const ativa = await buscarSimulacaoAtiva(signal);
  if (ativa?.status === "ATIVA") return ativa;

  const sess = lerSimulacaoSessao();
  if (sess?.status !== "ATIVA" || !sess.id) return null;

  try {
    const detalhe = await buscarDetalheSimulacao(sess.id, signal);
    if (detalhe.status === "ATIVA") return detalhe;
  } catch {
    // Detalhe indisponível ou simulação de outro usuário: segue sem rascunho.
  }

  return null;
}

/**
 * Id do rascunho editável para gravar e remover instruções.
 * Reconsulta GET /ativa; se não houver ATIVA, abre outro rascunho.
 */
export async function resolverIdSimulacaoParaInstrucao(): Promise<string | null> {
  const ativa = await buscarSimulacaoAtiva();
  if (ativa?.status === "ATIVA") {
    sincronizarSimulacaoSessao(ativa);
    return normalizarId(ativa.id);
  }

  limparSimulacaoSessao();
  const nova = await criarSimulacao();
  if (nova.status === "ATIVA") {
    sincronizarSimulacaoSessao(nova);
    return normalizarId(nova.id);
  }
  return null;
}
